import {useCallback, useContext} from 'react';

import {ValidationContext} from 'src/contexts/validation-context';
import {useCommonDataHook} from 'src/hooks/use-common-data-hook';
import {getExceptDays, saveExceptDays} from 'src/openapi/admin/apis/LifeAgentApi';
import {useAppDispatch} from 'src/store/hooks';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import {
    EXCEPT_DAY_CONTROL, EXCEPT_DAY_DUPLICATE_ERROR,
    EXCEPT_DAY_EMPTY_ERROR,
    EXCEPT_DAY_EMPTY_MESSAGE,
} from './constants';

export const useExceptDays = () => {
    const apiDispatch = useAppDispatch();
    const {addError, validationErrors, removeError} = useContext(ValidationContext);

    const getDispatchAction = useCallback(async () => {
        const exceptDaysResponse = await apiDispatch(getExceptDays());
        if (exceptDaysResponse.payload.success && !exceptDaysResponse.payload.data.length) {
            const payload: {success: true, error: false, data: string[]} = {
                success: true,
                error: false,
                data: [''],
            };

            return Promise.resolve({type: '', payload});
        } else {
            return Promise.resolve({type: '', payload: exceptDaysResponse.payload});
        }
    }, [apiDispatch]);

    const saveDispatchAction = useCallback((requestBody: string[]) => apiDispatch(
        saveExceptDays({requestBody: requestBody.filter(Boolean)}),
    ), [apiDispatch]);

    const editValidation = useCallback((updatedDays: string[]) => {
        updatedDays.forEach((validationCandidate, id) => {
            if (validationCandidate) {
                removeError(`${EXCEPT_DAY_CONTROL}-${id}`, [EXCEPT_DAY_EMPTY_ERROR]);
            }

            if (!validationCandidate || updatedDays.filter(day => day === validationCandidate).length === 1) {
                removeError(`${EXCEPT_DAY_CONTROL}-${id}`, [EXCEPT_DAY_DUPLICATE_ERROR]);
            }
        });
    }, [removeError]);

    const saveValidation = useCallback((data: string[]) => {
        let valid = true;
        if (isDeepEqual({prev: data, current: ['']})) {
            return valid;
        }

        data.forEach((day, index) => {
            if (!day) {
                valid = false;

                addError({
                    controlId: `${EXCEPT_DAY_CONTROL}-${index}`,
                    code: EXCEPT_DAY_EMPTY_ERROR,
                    message: EXCEPT_DAY_EMPTY_MESSAGE,
                });
            }
        }, []);

        if (validationErrors.filter(({code}) => code === EXCEPT_DAY_DUPLICATE_ERROR).length) {
            valid = false;
        }

        return valid;
    }, [validationErrors, addError]);

    return useCommonDataHook({
        initValue: [],
        getDispatchAction,
        editValidation,
        saveDispatchAction,
        saveValidation,
    });
};
